import {
  getClient,
  employerContentClient,
  models,
  getTasksClient,
} from '../cms-client';
import envConfig from '../env-config';
import { log } from '../log';
import {
  enums,
  getEREnvironmentName,
  formatSpecialBenefits,
  formatTasks,
  formatCustomTask,
  formatPostIntakeContent,
  formatIntakeList,
  get,
} from '..';
import {
  IContentItem,
  DeliveryClient,
  ElementModels,
  Responses,
  MultipleItemsQuery,
  ClientTypes,
} from '@kontent-ai/delivery-sdk';
import { IKenticoData } from '../../contexts/kentico-data-context';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

dayjs.extend(isBetween);

const cachedData = {} as { [key: string]: any };

export interface Cache {
  get<T>(key: string): T | undefined;
  set<T>(key: string, value: T): void;
  clear(): void;
}

let cache: Cache = {
  get: <T>(k: string) => cachedData[k] as T | undefined,
  set: <T>(k: string, v: T) => {
    cachedData[k] = v;
  },
  clear: () => {
    Object.keys(cachedData).forEach((k) => delete cachedData[k]);
  },
};

// Test-only hooks to to use cacheManager in tests
export const __setCache = (c: Cache) => {
  cache = c;
};
export const __clearCache = () => {
  cache.clear();
};

/*******************************************************************************
 * remote-config-manager.js is a collection of functions to assist with
 * fetching remote configuration and making it available to the app
 ******************************************************************************/

const envCodename = envConfig.upEnvironment.toLowerCase().replace('.', '_');
// const log = new Logger\('remote-config-manager');

type Fetching = {
  [key in enums.CacheKeyType]?: boolean;
};

const fetching: Fetching = {};

/** Domain Models for Cache Storage */
export interface CallCenterDomainModel {
  allDay: boolean;
  start: Date | null;
  end: Date | null;
  bannerMessage: string;
}

export interface IFeatureFlagMap {
  feedback: boolean;
  show_tasks: boolean;
  absence_history: boolean;
  enable_stepup_authn: boolean;
  [name: string]: boolean;
}

export interface IDynamicContentItem {
  [name: string]: string;
}

export interface IDynamicContent {
  [name: string]: IDynamicContentItem;
}

export interface ILocalizedString {
  scope: string;
  translatedText: string;
}

export interface ILocalizedStrings {
  [name: string]: ILocalizedString;
}

export interface ISplashConfig {
  showSplash: boolean;
  content?: string;
  nextStartDateTime?: Date;
}

export type EmployerContentItemType =
  | string
  | ISpecialBenefit[]
  | ElementModels.IElement<any>[]
  | IEmployerCustomTask[]
  | INextStep[];

export interface IEmployerContent {
  [name: string]: EmployerContentItemType;
}

export interface ISpecialBenefit {
  content?: string;
  featureImage?: ElementModels.AssetModel;
  thumbnail?: ElementModels.AssetModel;
  headline?: string;
  intakes?: Array<string>;
  summary?: string;
}
export interface ITask {
  type?: string;
  name?: string;
  description?: string;
  action?: string;
  customTask?: boolean;
  form?: string;
}

export interface IEmployerCustomTask {
  type: string;
  name: string;
  description: string;
  action: string;
  form: string;
}
export interface INextStep {
  title: any;
  description: any;
  thumbnail: any;
  optionsTemplate: any;
  link: any;
  type: any;
  order: any;
}
export interface IEmployerNextStep {
  title: any;
  description: any;
  thumbnail: any;
  optionsTemplate: any;
  link: any;
  type: any;
  intakes: any;
}

export const DEFAULT_FEATURE_FLAGS: IFeatureFlagMap = {
  feedback: false,
  show_tasks: false,
  absence_history: false,
  enable_stepup_authn: false,
};

export interface ITasksConfig {
  taskTitle: string;
  taskCodename: string;
  category: ElementModels.MultipleChoiceOption;
  description: string;
  shortDescription: string;
  frequentlyAskedQuestions: {
    question: string;
    response: string;
  }[];
  tags: ElementModels.TaxonomyTerm[];
  formCodename: string;
}

// typescript magic to require at least one of the provided keys
type AtLeastOne<T, U = { [K in keyof T]: Pick<T, K> }> = Partial<T> &
  U[keyof U];

type GetKontentRequiredParameters = {
  cacheKey: enums.CacheKeyType;
};

type GetKontentEitherRequiredParameters = {
  kontentQuery: MultipleItemsQuery<ClientTypes, IContentItem>;
  kontentType: string;
};

type GetKontentOptionalParameters = {
  kontentContainsFilters?: { element: string; value: string[] }[];
  kontentEqualsFilter?: { element: string; value: string };
  kontentGreaterThanFilter?: { element: string; value: string };
  kontentLimitParameter?: number;
  kontentElementsParameters?: string[];
  kontentDepth?: number;
  cacheSuffix?: string;
  cacheExpirationMinutes?: number;
  cmsClient?: DeliveryClient;
};

// for use in the main getKontent() function
type GetKontentParameters = GetKontentRequiredParameters &
  AtLeastOne<GetKontentEitherRequiredParameters> &
  GetKontentOptionalParameters;

// for use in the buildKontentQuery() function
type BuildKontentQueryParameters = GetKontentRequiredParameters &
  Pick<GetKontentEitherRequiredParameters, 'kontentType'> &
  GetKontentOptionalParameters;

export const buildKontentQuery = async <KontentType extends IContentItem>({
  kontentType,
  kontentContainsFilters = [],
  kontentEqualsFilter,
  kontentGreaterThanFilter,
  kontentLimitParameter,
  kontentElementsParameters,
  kontentDepth,
  cmsClient,
}: BuildKontentQueryParameters) => {
  const client = cmsClient ?? (await getClient());
  let kontentQuery = client.items<KontentType>().type(kontentType);

  for (const kontentContainsFilter of kontentContainsFilters) {
    kontentQuery = kontentQuery.containsFilter(
      kontentContainsFilter.element,
      kontentContainsFilter.value,
    );
  }

  if (kontentEqualsFilter) {
    kontentQuery = kontentQuery.equalsFilter(
      kontentEqualsFilter.element,
      kontentEqualsFilter.value,
    );
  }

  if (kontentGreaterThanFilter) {
    kontentQuery = kontentQuery.greaterThanFilter(
      kontentGreaterThanFilter.element,
      kontentGreaterThanFilter.value,
    );
  }

  if (kontentLimitParameter) {
    kontentQuery = kontentQuery.limitParameter(kontentLimitParameter);
  }

  if (kontentElementsParameters) {
    kontentQuery = kontentQuery.elementsParameter(kontentElementsParameters);
  }

  if (kontentDepth) {
    kontentQuery = kontentQuery.depthParameter(kontentDepth);
  }

  return kontentQuery;
};

export const getKontent = async <KontentType extends IContentItem>(
  p: GetKontentParameters,
) => {
  const { kontentType, cacheKey, cacheSuffix } = p;

  log.debug(`getKontent(${kontentType})...`);

  while (fetching[cacheKey]) {
    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  const calculatedCacheKey = cacheSuffix
    ? `${cacheKey}_${cacheSuffix}`
    : cacheKey;

  let cachedItem =
    cache.get<Responses.IListContentItemsResponse<KontentType>>(
      calculatedCacheKey,
    );

  if (!cachedItem) {
    // set this to avoid multiple network calls for the same cache key
    fetching[cacheKey] = true;

    // use the provided query, or build one from parameters
    const kontentQueryInstance =
      (p.kontentQuery as MultipleItemsQuery<ClientTypes, KontentType>) ||
      (await buildKontentQuery<KontentType>(p as BuildKontentQueryParameters));

    // network call
    const result = await kontentQueryInstance.toPromise();

    try {
      cache.set(calculatedCacheKey, result.data);
    } catch (error) {
      log.error(error);
    }

    cachedItem = result.data;
  }

  fetching[cacheKey] = false;
  return cachedItem;
};

/**
 * Retrieves asset config from Kentico, maps it, and caches it
 * @returns {object} asset configuration key/value pairs
 */
const getAllAssetReferences = async () => {
  log.debug('getAllAssetReferences...');

  const assetRefs: IKenticoData['assets'] = {};

  const result = await getKontent<models.AssetContainer>({
    kontentType: 'asset_container',
    cacheKey: 'asset_references',
    kontentEqualsFilter: {
      element: 'system.codename',
      value: enums.CodeNames.allAssets,
    },
  });

  const firstItem = result?.items?.[0];

  if (!firstItem) {
    throw Error('Unable to get assets');
  }

  for (const asset of firstItem.elements.assets.value) {
    assetRefs[asset.name] = asset;
  }

  return assetRefs;
};

/**
 * Retrieves call center closure config from Kentico
 * @param {Date} date to filter in greaterThanFilter()
 * @returns {object} form configuration key/value pairs
 */
const getCallCenterClosureConfig = async (endDate: Date) => {
  log.debug('getCallCenterClosureConfig...');

  const r = await getKontent<models.CallCenterClosure>({
    kontentType: 'call_center_closure',
    cacheKey: 'callCenterClosures',
    kontentGreaterThanFilter: {
      element: 'elements.end',
      value: endDate.toISOString(),
    },
  });

  // build domain model to store in cache
  return r.items.map((i) => ({
    allDay: i.elements.flags?.value.some(
      (f) => f.codename === enums.FormFlags.allDay,
    ),
    start: i.elements.start.value,
    end: i.elements.end.value,
    bannerMessage: i.elements.portal_unum_com_banner_message?.value ?? '',
  })) as CallCenterDomainModel[];
};

/**
 * Retrieves Start A Claim Page Content
 * @returns {object} start a claim page content
 */
const getStartAClaimContent = async () => {
  log.debug('getStartAClaimContent...');

  return (await getClient())
    .item<models.IntakeSelection>('intake_start')
    .depthParameter(3)
    .toPromise();
};

/**
 * Retrieves form config from Kentico
 * @returns {object} form configuration key/value pairs
 */
const getFormConfig = async (formName: string, depthParameter = 3) => {
  return getKontent<models.Form>({
    kontentType: 'form',
    cacheKey: 'form_config',
    cacheSuffix: formName,
    kontentEqualsFilter: {
      element: 'elements.path_match',
      value: formName,
    },
    kontentLimitParameter: 1,
    kontentDepth: depthParameter,
  });
};

const getExitPromptFormList = async () => {
  const result = await getKontent({
    kontentType: 'intakes_list',
    cacheKey: 'intakes_list',
    kontentDepth: 3,
  });

  for (const item of result.items) {
    if (item.elements.intakes) {
      return formatIntakeList(item.elements.intakes.value);
    }
  }

  return [];
};

/**
 * Retrieves simple remote config from Kentico, maps it, and caches it
 * @returns {object} remote configuration key/value pairs
 */
const getSimpleRemoteConfig = async () => {
  const remoteConfig: { [name: string]: string } = {};
  const r = await getKontent<models.SimpleRemoteConfigurationItem>({
    kontentType: 'simple_remote_configuration_item',
    cacheKey: 'remote_config',
    kontentElementsParameters: [envCodename],
  });

  for (const item of r.items) {
    const codename = item.system.codename;
    remoteConfig[codename] =
      item.elements[envCodename as keyof typeof item.elements]?.value ?? '';
  }

  return remoteConfig;
};

/**
 * Retrieves feature flags from Kentico, maps it, and caches it
 * @returns {object} feature flag key/value pairs
 */
const getFeatureFlags = async () => {
  const r = await getKontent<models.FeatureFlags>({
    kontentType: 'feature_flags',
    cacheKey: 'feature_flags',
    kontentElementsParameters: [envCodename],
  });

  const ff = DEFAULT_FEATURE_FLAGS;

  Object.keys(ff).forEach((k) => {
    ff[k] = r.items.some((i) =>
      i.elements[envCodename as keyof typeof i.elements]?.value.some(
        (f: ElementModels.MultipleChoiceOption) => f.codename === k,
      ),
    );
  });

  return ff;
};

/**
 * Retrieves dynamic content blobs from Kentico, maps it, and caches it
 * @returns {object} dynamic content blob
 */
const getDynamicContent = async (path: string) =>
  getKontent<models.ContentBlob>({
    kontentType: 'content_blob',
    cacheKey: 'content_blob',
    kontentEqualsFilter: {
      element: 'elements.path',
      value: path,
    },
    cacheSuffix: path.replace(new RegExp(/\//g), '_'),
  });

/**
 * Retrieves Localized String content items from Kentico and maps it
 * @returns {object} Localized String key/value pairs
 */
const getLocalizedStrings = async () => {
  log.debug('getLocalizedStrings...');

  const localizedStrings: ILocalizedStrings = {};
  const result = await getKontent<models.LocalizedString>({
    kontentType: 'localized_string',
    cacheKey: 'localized_string',
  });

  for (const item of result.items) {
    const codename = item.system.codename;
    localizedStrings[codename] = {
      scope: item?.elements?.scope?.value,
      translatedText: item?.elements?.translated_text?.value,
    };
  }

  return localizedStrings;
};

/**
 * Retrieves Dynamic Content type items from Kentico and maps it
 * @returns {object} Dynamic Content key/value pairs
 */
const getMarkdownContent = async () => {
  log.debug('getMarkdownContent...');

  const dynamicContent: IDynamicContent = {};
  const result = await getKontent<models.DynamicContent>({
    kontentType: 'dynamic_content',
    cacheKey: 'dynamic_content',
  });

  for (const item of result.items) {
    const codename = item.system.codename;
    dynamicContent[codename] = {};
    Object.keys(item.elements).forEach((key) => {
      // Convert snake_case element keys to camelCase so CMS templates
      // that reference e.g. "dcContentFull" continue to work after the
      // SDK upgrade (element keys are now snake_case in item.elements).
      const camelKey = key.replace(/([-_][a-z])/g, (g) =>
        g.toUpperCase().replace('-', '').replace('_', ''),
      );
      dynamicContent[codename][camelKey] = (item.elements as any)[key].value;
    });
  }

  return dynamicContent;
};

/**
 * Gets splash configuration from Kentico, maps it, and returns it
 * @returns {ISplashConfig}
 */
const getSplashConfig = async (includeFuture = false) => {
  log.debug('getSplashConfig...');
  const splashConfig: ISplashConfig = { showSplash: false };
  const now = dayjs().startOf('minute').toISOString();

  const querySplashes = (await getClient())
    .items<models.SplashConfig>()
    .type('splash_config')
    .containsFilter('elements.environments', [envCodename])
    .containsFilter('elements.recurring_splash', ['no'])
    .notEqualsFilter('system.codename', 'odyssei_auto_splash');

  const querySplashesRefined = includeFuture
    ? querySplashes.greaterThanFilter('elements.end_time', now)
    : querySplashes
        .lessThanFilter('elements.start_time', now)
        .greaterThanFilter('elements.end_time', now);

  const splashes = await getKontent({
    kontentQuery: querySplashesRefined,
    cacheKey: `splash_config_${includeFuture ? 'upcoming' : 'current'}`,
  });

  if (splashes.items.length) {
    splashConfig.content = splashes.items[0]?.elements?.content?.value;
    splashConfig.nextStartDateTime =
      splashes.items[0]?.elements?.startTime?.value || new Date();
    splashConfig.showSplash = true;
  }

  return splashConfig;
};

/**
 * Query-string key testers can append (e.g. `?splashbypass`) to skip the
 * outage splash/redirect during an outage.
 */
const SPLASH_BYPASS_PARAM = 'splashbypass';

/**
 * Determines whether the outage splash should be bypassed for testing.
 *
 * Appending the `?splashbypass` query param seeds a session-scoped flag so the
 * bypass survives the in-app redirect and subsequent navigation within the
 * tab; once seeded, the flag alone keeps the bypass active. Pass
 * `?splashbypass=false` to explicitly clear it.
 * @returns {boolean} true when the outage splash should be skipped
 */
const isSplashBypassed = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.has(SPLASH_BYPASS_PARAM)) {
      if (params.get(SPLASH_BYPASS_PARAM) === 'false') {
        sessionStorage.removeItem(enums.CacheKeys.splash);
      } else {
        sessionStorage.setItem(enums.CacheKeys.splash, 'bypass');
      }
    }
  } catch (error) {
    log.error('Unable to evaluate splash bypass', error);
  }

  return sessionStorage.getItem(enums.CacheKeys.splash) === 'bypass';
};

/**
 * Retrieves Employer Content items from Kentico and maps it
 * @returns {object} Employer Content key/value pairs
 */
const getEmployerContent = async (erId: string) => {
  log.debug('getEmployerContent...');
  const filterQueryLS = `elements.${getEREnvironmentName(
    envConfig.upEnvironment,
  )}`;

  const result = await getKontent<models.Employer>({
    kontentQuery: employerContentClient()
      .items<models.Employer>()
      .type('employer')
      .equalsFilter(filterQueryLS, erId)
      .depthParameter(3),
    cacheKey: 'employer_content',
  });

  if (!result.items[0]) {
    return {};
  }

  const item: models.Employer = result.items[0];
  const employerContent: IEmployerContent = {};

  //Loop through properties and format
  Object.keys(item.elements).forEach((key: string) => {
    const ie: any = item.elements;

    // Convert snake_case element keys to camelCase so consumers
    // that reference e.g. "specialBenefits" continue to work after
    // the SDK upgrade (element keys are now snake_case).
    const camelKey = key.replace(/([-_][a-z])/g, (g) =>
      g.toUpperCase().replace('-', '').replace('_', ''),
    );

    //handle text and unknown values
    let rootValue: EmployerContentItemType = ie[key].value || '';

    //handle linked items (special benefits, tasks, etc.)
    if (ie[key].linkedItems && Array.isArray(ie[key].linkedItems)) {
      rootValue = formatSpecialBenefits(ie[key].linkedItems);
    } else if (Array.isArray(ie[key].value)) {
      rootValue = formatSpecialBenefits(ie[key].value);
    }

    if (ie[key].name === 'Custom Tasks') {
      rootValue = formatCustomTask(ie[key].linkedItems || ie[key].value);
    }

    if (ie[key].name === 'Post-intake Content') {
      rootValue = formatPostIntakeContent(ie[key].linkedItems || ie[key].value);
    }

    //handle multiple choice
    if (ie[key].type === enums.ERContentTypes.multipleChoice) {
      rootValue = ie[key]?.value[0]?.name || '';
    }

    employerContent[camelKey] = rootValue;
  });

  return employerContent;
};

/**
 * Retrieves tasks from Kentico, maps it, and caches it
 * @returns {object} Task key/value pairs
 */
const getTasksMap = async () => {
  log.debug('getTasksMap...');
  const tasksMap = [];

  const result = await getKontent<models.Task>({
    kontentType: 'task',
    cacheKey: 'tasks-config',
  });

  for (const item of result.items) {
    if (!item) {
      break;
    } else {
      tasksMap.push(formatTasks(item));
    }
  }

  return tasksMap;
};

const getByBusinessRole = (items: models.tasks.Descriptionbybusinessrole[]) =>
  items.find((item) =>
    item.elements.businessrole?.linkedItems.find(
      (role) => role.elements.businessroleid?.value === '300000',
    ),
  );

const getMessageByBusinessRole = (
  item: models.tasks.Descriptionbybusinessrole[],
) => {
  const matchingItem = getByBusinessRole(item);
  return matchingItem?.elements?.message?.value ?? '';
};

const getFAQsByBusinessRole = (item: models.tasks.Faq[]) => {
  const filtered = item.filter((faq) =>
    faq.elements.businessrole?.linkedItems.find(
      (role) => role.elements.businessroleid?.value === '300000',
    ),
  );

  return filtered.map((faq) => ({
    question: faq.elements.question.value,
    response: faq.elements.response.value,
  }));
};

/**
 * Retrieves tasks from Kentico, maps it, and caches it
 * @returns {object} Task key/value pairs
 */
const getTasksConfig: () => Promise<ITasksConfig[]> = async () => {
  log.debug('getTasksConfig...');
  const tasksConfig: ITasksConfig[] = [];

  const result = await getKontent<models.tasks.Task>({
    kontentQuery: (await getTasksClient()).items<models.Task>().type('task'),
    cacheKey: 'tasks-config',
  });

  for (const item of result.items) {
    const formattedTask = {
      taskTitle: getMessageByBusinessRole(
        (item.elements.task_title?.linkedItems as any) || [],
      ),
      taskCodename: item.system.codename,
      category: item.elements?.category?.value?.[0],
      description: getMessageByBusinessRole(
        (item.elements.description?.linkedItems as any) || [],
      ),
      shortDescription: getMessageByBusinessRole(
        (item.elements.short_description?.linkedItems as any) || [],
      ),
      frequentlyAskedQuestions: getFAQsByBusinessRole(
        (item.elements.frequently_asked_questions?.linkedItems as any) || [],
      ),
      tags: get(item, 'elements.tags.value', []),
      formCodename: get(item, 'elements.form.value.0', ''),
    };
    tasksConfig.push(formattedTask as ITasksConfig);
  }

  return tasksConfig;
};

/**
 * Retrieves task status help text from Kentico, maps it, and caches it
 * @returns {object} Task key/value pairs
 */
const getTaskStatusConfig = async () => {
  log.debug('getTaskStatusConfig...');
  const statusConfig: {
    description: any;
    taskStatusId: any;
  }[] = [];

  const result = await getKontent<models.tasks.StatusHelpText>({
    kontentQuery: (await getTasksClient())
      .items<models.tasks.StatusHelpText>()
      .type('status_help_text')
      .depthParameter(3),
    cacheKey: 'task-status-help',
  });

  for (const item of result.items) {
    const statusHelp = {
      description: getMessageByBusinessRole(
        item.elements.help_text.linkedItems,
      ),
      taskStatusId: item.elements.task_status_id.value,
    };
    statusConfig.push(statusHelp);
  }

  return statusConfig;
};

/**
 * Retrieves claim status help text from Kentico, maps it, and caches it
 * @returns {object} Task key/value pairs
 */
const getClaimStatusConfig = async () => {
  log.debug('getClaimStatusConfig...');
  const claimStatusConfig: {
    message: any;
    status: any;
  }[] = [];

  const result = await getKontent<models.StatusHelpIcon>({
    kontentQuery: (await getClient())
      .items<models.StatusHelpIcon>()
      .type('status_help_icon'),
    cacheKey: 'status-help-icon',
  });

  for (const item of result.items) {
    const statusIcon = {
      message: item.elements.message.value,
      status: item.elements.status.value,
    };
    claimStatusConfig.push(statusIcon);
  }
  log.debug('status result - ', claimStatusConfig);
  return claimStatusConfig;
};

export const getEmployerContentSafely = async (erId?: string) => {
  if (erId) {
    try {
      return await getEmployerContent(erId);
    } catch (error) {
      log.error(error);
    }
  }

  return {};
};

/**
 * Environment-level Kentico data that is not tied to a specific person and is
 * shared by both the public landing page and the authenticated app shell.
 */
export type BaseKenticoData = Pick<
  IKenticoData,
  'assets' | 'featureFlags' | 'config' | 'localizedStrings' | 'dynamicContent'
>;

/**
 * Loads the environment-level Kentico data (assets, feature flags, config,
 * localized strings and markdown content). Results are served from the module
 * cache, so calling this from both the "/" landing route and the /app startup
 * sequence does not trigger duplicate network requests.
 * @returns {BaseKenticoData}
 */
export const loadKenticoData = async (): Promise<BaseKenticoData> => {
  const [assets, featureFlags, config, localizedStrings, dynamicContent] =
    await Promise.all([
      getAllAssetReferences(),
      getFeatureFlags(),
      getSimpleRemoteConfig(),
      getLocalizedStrings(),
      getMarkdownContent(),
    ]);

  return { assets, featureFlags, config, localizedStrings, dynamicContent };
};

/*******************************************************************************
 * exported api definition
 ******************************************************************************/
export {
  getCallCenterClosureConfig,
  getAllAssetReferences,
  getFormConfig,
  getSimpleRemoteConfig,
  getSplashConfig,
  isSplashBypassed,
  getFeatureFlags,
  getDynamicContent,
  getLocalizedStrings,
  getStartAClaimContent,
  getMarkdownContent,
  getEmployerContent,
  getTasksMap,
  getExitPromptFormList,
  getTasksConfig,
  getTaskStatusConfig,
  getClaimStatusConfig,
};
